import { inject, Injectable, Signal, signal } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { Observable, forkJoin, of, switchMap, throwError, tap, take } from 'rxjs';
import { IProject, IAPIListResponse, IListOptions, IProjectSigninConfig, IFooterPage } from '@newroom-connect/library/interfaces';
import { ChatbotType } from '@newroom-connect/library/enums';
import { ArrayHelper } from '@newroom-connect/library/helpers';

import { CacheManagerService } from '../cache/services/cache-manager.service';
import { CacheRegistry } from '../cache/cache-registry';
import { EntityService } from '../entity/entity.service';
import { LocalStorageService } from '../local-storage/local-storage.service';
import { LoggingService } from '../logging/logging.service';

export enum ProjectBroadcastEvent {
  UPDATED_PROJECT = 'updatedProject'
}

export interface ICreateProjectTranslationInput {
  languageCode: string;
  title: string;
}

export interface ICreateProjectInput {
  title: string; // Will be distributed across all translations
  translations: { code: string; }[]; // The language codes which are used for i81n properties of the project
  licenseId: string;
  templateId?: string; // The optional ID of the template to pre-populate the project data with
}

export interface IUpdateProjectTranslationInput {
  code: string;
  title: string;
}

export interface IUpdateProjectAnalyticsInput {
  id?: string | null;
  collectData: boolean;
  providers: IUpdateAnalyticsProviderInput[]
}

export interface IUpdateAnalyticsProviderInput {
  id?: string | null;
  enabled: boolean;
  analyticsKeyId: string;
  presetId: string;
}

export interface IUpdateProjectChatbotConfigInput {
  chatbotType: ChatbotType | null;
  chatbotKey?: string | null;
  chatbotSecret?: string | null;
}

export interface IUpdateProjectInput {
  translations?: IUpdateProjectTranslationInput[];
  startArea?: string; // The ID of the start area to set for the project.
  slug?: string;
  startDate?: string;
  endDate?: string;
  analytics?: IUpdateProjectAnalyticsInput,
  markets?: IUpdateProjectMarketsInput[],
  backgroundFileId?: string | null;
  imprintFileId?: string | null;
  privacyPolicyFileId?: string | null;
  footerPages?: IFooterPage[];
  isPublic?: boolean;
  navigation?: {
    hideLogoutButton?: boolean;
    hideLanguageSelector?: boolean;
  };
  chatbotConfig?: IUpdateProjectChatbotConfigInput | null;
}

export interface IUpdateProjectMarketsInput {
  id?: string;
  name: string;
  slugSuffix: string;
  languages: string[];
  markedForDelete: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class ProjectService extends EntityService {
  private readonly cacheManager = inject(CacheManagerService);
  private readonly cacheRegistry = inject(CacheRegistry);
  private readonly logger = inject(LoggingService);

  public searchableProperties = ['translations[].title', 'userLicense.license.title'];

  private currentProjectSig = signal<IProject | null>(null);
  private currentProjectSigninConfigSig = signal<IProjectSigninConfig | null>(null);
  private projectCache = new Map<string, IProject>();
  private projectSigninConfigCache = new Map<string, IProjectSigninConfig>();

  /**
   * @constructor
   *
   * @param localStorageService
   */
  constructor(
    private readonly localStorageService: LocalStorageService
  ) {
    super();
  }

  /**
   * Get the current project, fetching from server if necessary.
   * @returns Observable of the current project.
   */
  public getCurrentProject(): Observable<IProject | null> {
    const current = this.currentProjectSig();

    if (!current) {
      return of(null);
    }

    // If we only have the ID, fetch the full project
    if (Object.keys(current).length === 1 && current.id) {
      return this.getProject(current.id).pipe(
        tap(project => this.setCurrentProject(project))
      );
    }

    return of(current);
  }

  /**
   * Set the current project signal with the given project.
   *
   * Also set the ID of the current project in the local storage.
   * If the current project is `null`, remove the key from the local storage.
   *
   * @param project The project to set in the signal and the local storage.
   */
  public setCurrentProject(project: IProject | null): void {
    this.currentProjectSig.set(project);

    if (project) {
      this.localStorageService.set('currentProjectId', project.id);
    } else {
      this.localStorageService.remove('currentProjectId');
    }
  }

  /**
   *
   * @returns
   */
  public watchCurrentSignInConfig(): Signal<IProjectSigninConfig | null> {
    return this.currentProjectSigninConfigSig.asReadonly();
  }

  /**
   *
   * @returns
   */
  public watchCurrentProject(): Signal<IProject | null> {
    return this.currentProjectSig.asReadonly();
  }

  /**
   *
   * @param options
   *
   * @returns
   */
  public listProjects(options?: IListOptions): Observable<IAPIListResponse<IProject>> {
    return super.list<IProject>('projects', undefined, options);
  }

  /**
   *
   * @param projectId
   *
   * @returns
   */
  public getProject(projectId: string): Observable<IProject> {
    return this.apiService.get<IProject>(`projects/${projectId}`);
  }

  /**
   * Get project by slug with caching.
   * @param projectSlug The project slug to fetch.
   * @returns Observable of the project.
   */
  public getProjectBySlug(projectSlug: string): Observable<IProject> {
    // Check cache first
    const cached = this.projectCache.get(projectSlug);

    if (cached) {
      return of(cached);
    }

    return this.listProjects({ filters: { slug: projectSlug } }).pipe(
      switchMap(response => {
        if (ArrayHelper.isNotEmpty(response.data)) {
          const project = response.data[0];

          this.projectCache.set(projectSlug, project);
          return of(project);
        }

        return throwError(() => new HttpErrorResponse({
          error: 'Project not found',
          status: 404
        }));
      })
    );
  }

  /**
   *
   * @param input
   *
   * @returns
   */
  public createProject(input: ICreateProjectInput): Observable<IProject> {
    return this.apiService.post<IProject>('projects', input);
  }

  /**
   *
   * @param projectId
   * @param input
   *
   * @returns
   */
  public updateProject(projectId: string, input: IUpdateProjectInput): Observable<IProject> {
    return this.apiService.patch<IProject>(`projects/${projectId}`, input);
  }

  /**
   *
   * @param projectId
   *
   * @returns
   */
  public deleteProject(projectId: string): Observable<IProject> {
    return this.apiService.delete<IProject>(`projects/${projectId}`);
  }

  /**
   *
   * @param projectIds
   *
   * @returns
   */
  public deleteProjects(projectIds: string[]): Observable<IProject[]> {
    return forkJoin(projectIds.map(projectId => this.apiService.delete<IProject>(`projects/${projectId}`)));
  }

  /**
   *
   * @param projectSlug
   *
   * @returns
   */
  public getProjectSigninConfig(projectSlug: string): Observable<IProjectSigninConfig> {
    const cached = this.projectSigninConfigCache.get(projectSlug);

    if (cached) {
      return of(cached);
    }

    return this.apiService.get<IProjectSigninConfig>(`projects/${projectSlug}/signin-config`).pipe(
      tap(signInConfig => {
        this.projectSigninConfigCache.set(projectSlug, signInConfig);
        this.currentProjectSigninConfigSig.set(signInConfig);
      }),
      take(1)
    );
  }

  /**
   * @param project
   * @returns
   */
  public isProjectActive(project: IProject): boolean {
    return this.isProjectStarted(project) && !this.isProjectEnded(project);
  }

  /**
   * @param project
   * @returns
   */
  public isProjectEnded(project: IProject): boolean {
    const currentDate = new Date();

    return currentDate > new Date(project.endDate);
  }

  /**
   * @param project
   * @returns
   */
  public isProjectStarted(project: IProject): boolean {
    const currentDate = new Date();

    return currentDate >= new Date(project.startDate);
  }

  /**
   * Clears all web-app session caches on logout (in-memory project state and IndexedDB stores if any is enabled).
   *
   * @returns Observable that emits whether all registered cache stores were cleared.
   */
  public clearCachedData(): Observable<boolean> {
    // Clear in-memory project state
    this.currentProjectSig.set(null);
    this.currentProjectSigninConfigSig.set(null);
    this.projectCache.clear();
    this.projectSigninConfigCache.clear();

    // Clear IndexedDB if there are any caches to clear.
    this.cacheRegistry.reset();

    return this.cacheManager.clearAll();
  }
}
